import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import {
  groupByInfluencer,
  hasExclusionConflict,
  isAtMaxQuantity,
} from "../../../../domain/influencerService";
import useAvailableServicesForProduct from "../../../../hooks/useAvailableServicesForProduct";
import { usd } from "../../../../util/formatCurrency";
import { CheckCompactIcon } from "../Icons.jsx";
import ServiceCard from "./ServiceCard.jsx";
import ServiceInstance from "./ServiceInstance.jsx";
import s from "./ServiceSelector.module.css";

/*
 * Pure: why this service cannot be added right now, or undefined if it can.
 */
const disabledReasonFor = (service, selectedInstances) => {
  if (isAtMaxQuantity(service, selectedInstances)) {
    const max = service.maxQuantity ?? 1;
    return `Maximum of ${max} instance${max === 1 ? "" : "s"} reached`;
  }

  if (hasExclusionConflict(service, selectedInstances)) {
    return "Only one service from this group can be selected";
  }

  return undefined;
};

/*
 * Pure: services grouped by the signer who performs them, preserving the
 * order the API returned them in.
 */
const groupBySigner = (services) => {
  const byInfluencer = groupByInfluencer(services);

  // Object keys sort numerically for integer-like ids, so the signer order
  // comes from first appearance in the response rather than from the grouping.
  return [...new Set(services.map((service) => service.influencerId))].map(
    (influencerId) => ({
      influencerId,
      name: byInfluencer[influencerId][0].influencerName,
      services: byInfluencer[influencerId],
    }),
  );
};

/*
 * Pure: instances keyed by the service they belong to.
 */
const byServiceId = (instances) =>
  instances.reduce((acc, instance) => {
    (acc[instance.influencerServiceId] ??= []).push(instance);
    return acc;
  }, {});

function SelectedService({
  service,
  instances,
  onAdd,
  onRemove,
  onTextChange,
}) {
  const maxQuantity = service.maxQuantity ?? 1;
  const priceLabel = usd(service.totalPrice);
  const isSingleInstance = maxQuantity === 1;

  return (
    <div
      className={`${s.card} ${s.cardSelected}`}
      role="group"
      aria-label={`${service.label}, selected`}
    >
      <div className={s.cardHeader}>
        <span className={s.check}>
          <CheckCompactIcon className={s.checkIcon} />
        </span>
        <span className={s.info}>
          <span className={s.title}>{service.label}</span>
          {service.description && (
            <span className={s.desc}>{service.description}</span>
          )}
        </span>
        <span className={s.price}>+{priceLabel}</span>
      </div>

      <div className={s.expandedContent}>
        <div className={s.instanceGroup}>
          {instances.map((instance, index) => (
            <ServiceInstance
              key={instance.id}
              instance={instance}
              serviceLabel={service.label}
              // Several instances of the same service can be on screen, so the
              // remove buttons need distinguishable names.
              removeLabel={
                isSingleInstance
                  ? `Remove ${service.label}`
                  : `Remove ${service.label} ${index + 1}`
              }
              onTextChange={onTextChange}
              onRemove={onRemove}
            />
          ))}
        </div>

        {instances.length < maxQuantity && (
          <button
            type="button"
            className={s.addAnother}
            onClick={() => onAdd(service)}
            aria-label={`Add another ${service.label}`}
          >
            + Add another
          </button>
        )}
      </div>
    </div>
  );
}

SelectedService.propTypes = {
  service: PropTypes.object.isRequired,
  instances: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
};

export default function ServiceSelector({
  productId,
  services: selectedInstances,
  onAdd,
  onRemove,
  onTextChange,
}) {
  const { services: availableServices, isLoading } =
    useAvailableServicesForProduct(productId);
  const [announcement, setAnnouncement] = useState("");

  const handleAdd = useCallback(
    (service) => {
      onAdd({
        influencerServiceId: service.influencerServiceId,
        influencerId: service.influencerId,
        label: service.label,
        totalPrice: service.totalPrice,
        customerText: "",
        maxWordCount: service.maxWordCount,
        requiresTextInput: service.requiresTextInput,
        exclusionGroup: service.exclusionGroup,
      });
      setAnnouncement(`${service.label} added`);
    },
    [onAdd],
  );

  const handleRemove = useCallback(
    (instanceId) => {
      const removed = selectedInstances.find(
        (instance) => instance.id === instanceId,
      );
      onRemove(instanceId);
      setAnnouncement(removed ? `${removed.label} removed` : "Service removed");
    },
    [onRemove, selectedInstances],
  );

  const instancesByService = useMemo(
    () => byServiceId(selectedInstances),
    [selectedInstances],
  );

  const signerGroups = useMemo(
    () => groupBySigner(availableServices ?? []),
    [availableServices],
  );

  if (isLoading || !availableServices || availableServices.length === 0) {
    return null;
  }

  const isMultiSigner = signerGroups.length > 1;

  const renderService = (service) => {
    const instances = instancesByService[service.influencerServiceId] ?? [];

    return instances.length > 0 ? (
      <SelectedService
        key={service.influencerServiceId}
        service={service}
        instances={instances}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onTextChange={onTextChange}
      />
    ) : (
      <ServiceCard
        key={service.influencerServiceId}
        service={service}
        disabledReason={disabledReasonFor(service, selectedInstances)}
        onAdd={handleAdd}
      />
    );
  };

  return (
    <section className={s.root} aria-labelledby="services-heading">
      <div className={s.section}>
        <h3 className={s.sectionLabel} id="services-heading">
          Personalize Your Item
        </h3>

        {isMultiSigner ? (
          // Two signers can offer identically named services, so each group is
          // labelled with whose work it is.
          signerGroups.map((group) => {
            const legendId = `services-signer-${group.influencerId}`;

            return (
              // The legend is the native association; aria-labelledby restates
              // it because not every accessibility tree derives a fieldset's
              // name from its legend. Both resolve to the same text.
              <fieldset
                key={group.influencerId}
                className={s.signerGroup}
                aria-labelledby={legendId}
              >
                <legend id={legendId} className={s.signerLegend}>
                  Services from {group.name ?? "this signer"}
                </legend>
                <div className={s.list}>
                  {group.services.map(renderService)}
                </div>
              </fieldset>
            );
          })
        ) : (
          <div className={s.list}>{availableServices.map(renderService)}</div>
        )}
      </div>

      {/* Adding or removing a service changes the price and the form below it;
          neither is obvious to a screen reader without announcing it. */}
      <div className={s.srOnly} role="status" aria-live="polite">
        {announcement}
      </div>
    </section>
  );
}

ServiceSelector.propTypes = {
  productId: PropTypes.number.isRequired,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      influencerServiceId: PropTypes.number.isRequired,
      label: PropTypes.string,
    }),
  ).isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
};
